import React, { useState, useEffect } from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ChevronLeft, Plus, Minus, ShoppingBag, Trash2, Image as ImageIcon, Settings, Check, Link as LinkIcon, DollarSign, Percent, Clock, Tag, SlidersHorizontal, Copy, Box, Truck, List, X, Save, Sparkles, Loader2 } from 'lucide-react';
import { generateAIContent } from '../services/geminiService';
import ImagePickerModal from './ImagePickerModal';
import { ShippingSize } from '../types';
import ProductSummaryAI from './ProductSummaryAI';
import { GoogleGenAI } from '@google/genai';

const { useParams, useNavigate, Link, useSearchParams } = ReactRouterDOM as any;

const ProductDetailsPage: React.FC = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getProductById, getAllProducts, addToCart, isEditMode, updateProduct, updateSection, pages, duplicateProduct, config, favorites, toggleFavorite, recentlyViewed, addRecentlyViewed } = useApp();
  
  const [productData, setProductData] = useState<{ product: any, sectionId: string, pageId: string } | null>(null);
  const [parentSection, setParentSection] = useState<any>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isAttrManagerOpen, setIsAttrManagerOpen] = useState(false); // NEW: Attribute Manager Modal
  const [quantity, setQuantity] = useState(1);
  const [isSuggestingRelated, setIsSuggestingRelated] = useState(false);
  const [isFetchingAiParams, setIsFetchingAiParams] = useState(false);

  // Promotion State for Edit Mode
  const [promoType, setPromoType] = useState<'PERCENT' | 'FIXED'>('PERCENT');
  const [promoValue, setPromoValue] = useState<string>('');
  const [promoStart, setPromoStart] = useState<string>('');
  const [promoEnd, setPromoEnd] = useState<string>('');
  const [promoLimit, setPromoLimit] = useState<string>('');

  // Fetch product data including related items
  useEffect(() => {
    if (productId) {
      const found = getProductById(productId);
      if (found) {
        setProductData(found);
        
        // Find parent section to get filter definitions
        const page = pages.find(p => p.id === found.pageId);
        const section = page?.sections.find(s => s.id === found.sectionId);
        setParentSection(section);

          // Variant Selection Logic
        if (found.product.variants && found.product.variants.length > 0) {
            // Check for preselect param in URL (e.g. ?preselect=30L)
            const preselect = searchParams.get('preselect');
            let matchedVariant = found.product.variants[0]; // Default to first

            if (preselect) {
                // Try to find matching variant by Name or Attribute
                const match = found.product.variants.find((v: any) => {
                    const nameMatch = v.name.toLowerCase().includes(preselect.toLowerCase());
                    const attrMatch = v.attributes 
                        ? Object.values(v.attributes).some((val: any) => String(val).toLowerCase() === preselect.toLowerCase())
                        : false;
                    return nameMatch || attrMatch;
                });
                if (match) matchedVariant = match;
            }
            setSelectedVariantId(matchedVariant.id);
        }
        
        // Add to recently viewed
        if (productId) {
            addRecentlyViewed(productId);
        }
      }
      // Reset scroll when switching products
      window.scrollTo(0, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, searchParams.get('preselect')]);

  if (!productData) {
      return (
          <div className="min-h-[50vh] flex items-center justify-center">
              <p className="text-gray-400">Produkt nie został znaleziony.</p>
          </div>
      );
  }

  const { product, pageId } = productData;
  const page = pages.find(p => p.id === pageId);
  // Ensure we have the latest version of the section from the global state
  const sectionId = productData.sectionId;
  const currentSection = page?.sections.find(s => s.id === sectionId) || parentSection;

  // Merge currentSection and config.globalAttributes
  const localFilters = currentSection?.filterCategories || [];
  const globalFilters = config.globalAttributes?.filter(a => a.categoryIds?.includes(currentSection?.id)) || [];
  const filtersMap = new Map();
  [...globalFilters, ...localFilters].forEach(f => filtersMap.set(f.key, f));
  const activeFiltersForProduct = Array.from(filtersMap.values());

  const backLink = page ? page.slug : '/kolekcja';

  const gallery = product.gallery && product.gallery.length > 0 ? product.gallery : [product.imageUrl];
  
  // Resolve current price based on variant
  const selectedVariant = product.variants?.find((v: any) => v.id === selectedVariantId);
  const currentPriceStr = selectedVariant ? selectedVariant.price : product.price;
  const purchasePriceStr = selectedVariant ? selectedVariant.purchasePrice : product.purchasePrice;
  const basePriceStr = selectedVariant ? selectedVariant.basePrice : product.basePrice || product.price;

  // Calculate financials
  const currentPrice = parseFloat(currentPriceStr || '0');
  const purchasePrice = parseFloat(purchasePriceStr || '0');
  const profit = currentPrice - purchasePrice;
  const margin = currentPrice > 0 ? ((profit / currentPrice) * 100).toFixed(1) : '0';

  // Shipping Size (default to parcel locker if not set)
  const shippingSize: ShippingSize = product.shippingSize || 'parcel_locker';

  const allProducts = getAllProducts();
  let relatedProducts = (product.relatedProductIds || [])
        .map((id: string) => allProducts.find((p: any) => p.id === id))
        .filter(Boolean);

  // If no related products set manually, suggest some from same section
  if (relatedProducts.length === 0 && currentSection?.items) {
      relatedProducts = currentSection.items
          .filter((p: any) => p.id !== product.id)
          .slice(0, 4);
  }

  const recentlyViewedProducts = recentlyViewed
      .filter((id: string) => id !== product.id)
      .map((id: string) => allProducts.find((p:any) => p.id === id))
      .filter(Boolean)
      .slice(0, 4);

  const isFavorite = favorites.includes(product.id);

  // --- Handlers ---

  const handleAddToCart = () => {
      addToCart({
          id: `${product.id}-${selectedVariantId || 'base'}`,
          productId: product.id,
          title: product.title,
          variantName: selectedVariant?.name,
          price: parseFloat(currentPriceStr),
          imageUrl: gallery[0],
          quantity: quantity,
          shippingSize: shippingSize
      });
  };

  const handleUpdateField = (field: string, value: any) => {
      updateProduct(product.id, { [field]: value });
  };

  const handleUpdateAttribute = (key: string, value: string) => {
      const currentAttrs = product.attributes || {};
      handleUpdateField('attributes', { ...currentAttrs, [key]: value });
  };

  const handleAddVariant = () => {
      const newVariants = [...(product.variants || [])];
      newVariants.push({
          id: `var-${Date.now()}`,
          name: 'Nowy wariant',
          price: product.price,
          purchasePrice: product.purchasePrice || '0',
          available: true,
          attributes: {}
      });
      handleUpdateField('variants', newVariants);
  };

  const handleRemoveVariant = (variantId: string) => {
      const newVariants = product.variants.filter((v: any) => v.id !== variantId);
      handleUpdateField('variants', newVariants);
      if (selectedVariantId === variantId) setSelectedVariantId(null);
  };

  const handleUpdateVariant = (variantId: string, field: string, value: any) => {
      const newVariants = product.variants.map((v: any) => 
          v.id === variantId ? { ...v, [field]: value } : v
      );
      handleUpdateField('variants', newVariants);
  };

  // NEW: Update specific attribute inside a variant
  const handleUpdateVariantAttribute = (variantId: string, key: string, value: string) => {
      const newVariants = product.variants.map((v: any) => {
          if (v.id === variantId) {
              const newAttrs = { ...(v.attributes || {}), [key]: value };
              return { ...v, attributes: newAttrs };
          }
          return v;
      });
      handleUpdateField('variants', newVariants);
  };
  
  const toggleRelatedProduct = (targetId: string) => {
      const currentRelated = product.relatedProductIds || [];
      let newRelated;
      if (currentRelated.includes(targetId)) {
          newRelated = currentRelated.filter((id: string) => id !== targetId);
      } else {
          newRelated = [...currentRelated, targetId];
      }
      handleUpdateField('relatedProductIds', newRelated);
  };

  const handleAiAutoFillParams = async () => {
      if (!activeFiltersForProduct || activeFiltersForProduct.length === 0) {
          alert('Aby AI mogło dopasować cechy, najpierw zdefiniuj możliwe filtry/cechy dla tej sekcji w ustawieniach głównych wiersza.');
      }
      
      setIsFetchingAiParams(true);
      try {
          const prompt = `Jesteś ekspertem e-commerce i wyszukujesz precyzyjne informacje o produktach. Wyszukaj w internecie informacje o produkcie: "${product.title}".
Twoim zadaniem jest ułożenie dwóch wersji opisu produktu i dopasowanie jego realnych parametrów do listy dozwolonych cech w tym sklepie.

Dostępne, zdefiniowane kategorie cech w systemie to (klucz, nazwa, dostępne wartości):
${JSON.stringify(activeFiltersForProduct.map((f: any) => ({ klucz: f.key, etykieta: f.label, opcje_wyboru: f.options || [] })))}

Zasady:
1. "description" powinno zawierać KRÓTKI (2-3 zdania), naturalny chwytliwy opis w CZYSTYM TEKŚCIE (bez znaczników HTML).
2. "longDescription" powinno zawierać PEŁNY, rozbudowany opis produktu sformatowany w HTML (znacznik <p>, <ul>, <li>, <strong>). Zadbaj, aby treść w <p> była wyjustowana, np. używając <p style="text-align: justify;">.
3. Zwróć obiekt "attributes", w którym do odpowiednich dopasowanych "kluczy" (z powyższej listy zgłoszonych przez podane filtry jako "klucz") podstawisz pasującą do modelu wartość z "opcje_wyboru".
Nie podawaj atrybutów dla których nie potrafisz z pewnością znaleźć danych wyszukaniem.

Zwróć odpowiedź w CZYSTYM formacie JSON (bez bloków code markdown):
{
  "description": "Krótki opis bez HTML...",
  "longDescription": "<p style=\\"text-align: justify;\\">Rozbudowany opis w HTML...</p>",
  "attributes": { "pojemnosc-zloza": "25L" }
}`;

          const payload = {
              model: 'gemini-3.1-flash-lite',
              contents: [{ role: 'user', parts: [{ text: prompt }] }],
              generationConfig: { responseMimeType: 'application/json' },
              tools: [{ googleSearch: {} }]
          };

          const responseStream = await fetch('api/generate_ai.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });

          if (!responseStream.ok) {
              let errText = await responseStream.text();
              throw new Error(`Błąd API: ${responseStream.status} ${errText}`);
          }

          const responseData = await responseStream.json();
          const responseText = responseData.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
          
          const data = JSON.parse(responseText);
          if (data.description && typeof data.description === 'string') {
              handleUpdateField('description', data.description);
          }
          if (data.longDescription && typeof data.longDescription === 'string') {
              handleUpdateField('longDescription', data.longDescription);
          }
          if (data.attributes && typeof data.attributes === 'object') {
              // Merge attributes cleanly instead of total override if possible.
              const mergedAttrs = { ...(product.attributes || {}), ...data.attributes };
              handleUpdateField('attributes', mergedAttrs);
          }
      } catch (e) {
          console.error("AI Params Info Error:", e);
          alert('Wystąpił błąd podczas używania AI: ' + (e as any).message);
      } finally {
          setIsFetchingAiParams(false);
      }
  };

  const handleSuggestRelatedProducts = async () => {
      setIsSuggestingRelated(true);
      try {
          const otherProducts = allProducts
              .filter((p: any) => p.id !== product.id)
              .map((p: any) => ({ id: p.id, title: p.title, description: p.description }));
          
          const prompt = `Jesteś systemem rekomendacyjnym sklepu e-commerce. Mamy produkt główny: 
Nazwa: ${product.title}
Opis: ${product.description}
Atrybuty: ${JSON.stringify(product.attributes || {})}

Twoje zadanie: z poniższej listy produktów wybierz od 2 do 4 produktów, które najlepiej do niego pasują jako produkty powiązane (np. akcesoria, produkty komplementarne, logiczne rozszerzenia).

Lista dostępnych produktów:
${JSON.stringify(otherProducts)}

Zwróć odpowiedź w czystym JSON bez markdown, podając tablicę wybranych ID:
{ "recommendedIds": ["id1", "id2"] }`;

          const payload = {
              model: 'gemini-3.1-flash-lite',
              contents: [{ role: 'user', parts: [{ text: prompt }] }],
              generationConfig: { responseMimeType: 'application/json' }
          };

          const responseStream = await fetch('api/generate_ai.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });

          if (!responseStream.ok) {
              let errText = await responseStream.text();
              throw new Error(`Błąd API: ${responseStream.status} ${errText}`);
          }

          const responseData = await responseStream.json();
          const responseText = responseData.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
          
          const data = JSON.parse(responseText);
          if (data.recommendedIds && Array.isArray(data.recommendedIds)) {
              handleUpdateField('relatedProductIds', data.recommendedIds);
          }
      } catch (e) {
          console.error("AI Related Suggestion Error:", e);
      } finally {
          setIsSuggestingRelated(false);
      }
  };

  const handleImageSave = (urlOrUrls: string | string[]) => {
      const newUrls = Array.isArray(urlOrUrls) ? urlOrUrls : [urlOrUrls];
      let newGallery = [...gallery];
      
      // If we are replacing existing index (and it's a single update)
      if (activeImageIndex < newGallery.length && !Array.isArray(urlOrUrls)) {
          newGallery[activeImageIndex] = newUrls[0];
      } else {
          // If adding new (or multiple)
          // Avoid duplicates if possible but for images usually we just append
          newGallery = [...newGallery, ...newUrls];
      }
      
      handleUpdateField('gallery', newGallery);
      handleUpdateField('imageUrl', newGallery[0]); // Update main thumb
      setIsImageModalOpen(false);
  };
  
  const handleRemoveImage = (index: number) => {
      const newGallery = gallery.filter((_: any, i: number) => i !== index);
      if (newGallery.length === 0) return; // Don't allow empty
      handleUpdateField('gallery', newGallery);
      handleUpdateField('imageUrl', newGallery[0]);
      if (activeImageIndex >= newGallery.length) setActiveImageIndex(0);
  };

  const handleDuplicate = () => {
      if (window.confirm('Czy na pewno chcesz utworzyć kopię tego produktu?')) {
          const newId = duplicateProduct(product.id);
          if (newId) {
              navigate(`/product/${newId}`);
          } else {
              alert('Nie udało się skopiować produktu.');
          }
      }
  };

  // --- Pricing Logic ---

  const applyDiscount = () => {
     const value = parseFloat(promoValue);
     if (isNaN(value) || value <= 0) return alert("Podaj poprawną wartość obniżki");

     // Ensure we have a base price stored
     const originalPrice = product.basePrice || product.price;
     if (!product.basePrice) {
         handleUpdateField('basePrice', originalPrice);
     }

     let newPrice = 0;
     const base = parseFloat(originalPrice);

     if (promoType === 'PERCENT') {
         newPrice = base * (1 - value / 100);
     } else {
         newPrice = base - value;
     }

     if (newPrice < 0) newPrice = 0;

     // Update fields
     handleUpdateField('price', Math.round(newPrice).toString());
     handleUpdateField('oldPrice', originalPrice);
     handleUpdateField('discountConfig', {
         isActive: true,
         type: promoType,
         value: value,
         startDate: promoStart,
         endDate: promoEnd,
         quantityLimit: parseInt(promoLimit) || null
     });
     
     alert(`Promocja aktywna! Nowa cena: ${Math.round(newPrice)} PLN`);
  };

  const removeDiscount = () => {
      // Revert to base price
      if (product.basePrice) {
          handleUpdateField('price', product.basePrice);
      } else if (product.oldPrice) {
           handleUpdateField('price', product.oldPrice);
      }
      handleUpdateField('oldPrice', null);
      handleUpdateField('discountConfig', { isActive: false });
  };

  return (
    <div className="container mx-auto px-6 py-12 animate-fade-in bg-[#fcfcfc]">
        {/* Breadcrumb / Back - Fixed to link to parent page */}
        <Link to={backLink} className="flex items-center text-xs text-gray-500 hover:text-black uppercase tracking-wider mb-8 transition-colors inline-flex">
            <ChevronLeft size={14} /> Powrót do: {page?.title || 'Kolekcja'}
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            
            {/* Left Column: Images */}
            <div className="space-y-6">
                <div className="aspect-[4/5] bg-gray-50 relative overflow-hidden group">
                    <img 
                        src={gallery[activeImageIndex]} 
                        className="w-full h-full object-cover mix-blend-multiply transition-all duration-500"
                        alt={product.title}
                    />
                    {isEditMode && (
                        <button 
                            onClick={() => setIsImageModalOpen(true)}
                            className="absolute top-4 right-4 bg-white/90 text-black p-2 shadow-sm rounded-full hover:bg-white"
                        >
                            <ImageIcon size={16}/>
                        </button>
                    )}
                </div>
                {/* Thumbnails */}
                {(gallery.length > 1 || isEditMode) && (
                    <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                        {gallery.map((img: string, idx: number) => (
                            <div 
                                key={idx} 
                                onClick={() => setActiveImageIndex(idx)}
                                className={`w-20 h-24 flex-shrink-0 cursor-pointer border relative ${activeImageIndex === idx ? 'border-black' : 'border-transparent hover:border-gray-300'}`}
                            >
                                <img src={img} className="w-full h-full object-cover"/>
                                {isEditMode && gallery.length > 1 && (
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleRemoveImage(idx); }}
                                        className="absolute top-0 right-0 bg-red-500 text-white p-0.5"
                                    >
                                        <XIcon size={10} />
                                    </button>
                                )}
                            </div>
                        ))}
                        {isEditMode && (
                            <button 
                                onClick={() => { setActiveImageIndex(gallery.length); setIsImageModalOpen(true); }}
                                className="w-20 h-24 flex items-center justify-center border border-dashed border-gray-300 text-gray-400 hover:text-black hover:border-black"
                            >
                                <Plus size={20}/>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Right Column: Details */}
            <div className="flex flex-col">
                <div className="border-b border-gray-200 pb-8 mb-8">
                     {isEditMode ? (
                         <input 
                            value={product.title} 
                            onChange={(e) => handleUpdateField('title', e.target.value)}
                            className="text-4xl font-serif text-gray-900 w-full mb-2 bg-transparent border-b border-blue-200"
                         />
                     ) : (
                         <div className="flex justify-between items-start">
                             <h1 className="text-4xl font-serif text-gray-900 mb-2">{product.title}</h1>
                             <button 
                                 onClick={() => toggleFavorite(product.id)}
                                 className={`p-3 rounded-full transition-all duration-300 ${isFavorite ? 'bg-red-50 text-red-500 hover:bg-red-100 shadow-sm' : 'bg-transparent text-gray-400 hover:text-red-400 hover:bg-gray-50'}`}
                             >
                                 <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill={isFavorite ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
                             </button>
                         </div>
                     )}
                     
                     {/* Rating Display */}
                     {!isEditMode && product.rating !== undefined && (
                         <div className="flex items-center gap-2 mb-4">
                             <div className="flex text-yellow-400">
                                 {[1,2,3,4,5].map(star => (
                                     <svg key={star} xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill={star <= product.rating! ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                 ))}
                             </div>
                             <a href="#reviews" className="text-xs text-gray-500 hover:text-black underline-offset-4 hover:underline">
                                 ({product.reviewsCount || 0} opinii)
                             </a>
                         </div>
                     )}

                     <div className="flex items-center gap-4 mb-6">
                         <span className="text-2xl font-medium text-gray-900">{currentPriceStr} PLN</span>
                         {product.oldPrice && <span className="text-gray-400 line-through">{product.oldPrice} PLN</span>}
                         {product.discountConfig?.isActive && (
                             <span className="bg-red-50 text-red-600 text-[10px] font-bold px-2 py-1 uppercase tracking-wider rounded">
                                 {product.discountConfig.type === 'PERCENT' ? `-${product.discountConfig.value}%` : 'Wyprzedaż'}
                             </span>
                         )}
                     </div>

                     {/* Delivery Info Badge */}
                     <div className="mb-6 flex gap-2">
                         {shippingSize === 'courier_only' ? (
                             <span className="flex items-center gap-1 text-[10px] bg-orange-100 text-orange-800 px-2 py-1 rounded font-bold uppercase tracking-wider">
                                 <Truck size={12}/> Tylko Kurier (Gabaryt)
                             </span>
                         ) : (
                             <span className="flex items-center gap-1 text-[10px] bg-green-100 text-green-800 px-2 py-1 rounded font-bold uppercase tracking-wider">
                                 <Box size={12}/> Paczkomat / Kurier
                             </span>
                         )}
                     </div>

                     <div className="prose prose-sm text-gray-500 leading-relaxed mb-6">
                         {isEditMode ? (
                             <textarea 
                                value={product.description}
                                onChange={(e) => handleUpdateField('description', e.target.value)}
                                className="w-full h-20 border border-gray-200 p-2 text-sm"
                             />
                         ) : (
                             <p>{product.description}</p>
                         )}
                     </div>

                     {/* Variants Selector */}
                     {product.variants && product.variants.length > 0 && (
                         <div className="mb-8">
                             <span className="text-xs font-bold uppercase tracking-widest text-gray-900 block mb-3">Wariant</span>
                             <div className="flex flex-wrap gap-2">
                                 {product.variants.map((v: any) => (
                                     <button
                                         key={v.id}
                                         onClick={() => setSelectedVariantId(v.id)}
                                         className={`px-4 py-2 text-sm border transition-all ${
                                             selectedVariantId === v.id 
                                             ? 'border-black bg-black text-white' 
                                             : 'border-gray-200 text-gray-600 hover:border-gray-400'
                                         }`}
                                     >
                                         {v.name}
                                     </button>
                                 ))}
                             </div>
                         </div>
                     )}

                     {!isEditMode && (
                        <ProductSummaryAI 
                            productName={product.title} 
                            productDescription={product.description} 
                            attributes={product.attributes} 
                        />
                     )}

                     {/* Add to Cart Actions */}
                     <div className="flex gap-4 items-center">
                         <div className="flex items-center border border-gray-200 h-12 w-32">
                             <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="flex-1 h-full hover:bg-gray-50 flex items-center justify-center text-gray-500"><Minus size={14}/></button>
                             <span className="flex-1 text-center font-medium text-gray-900">{quantity}</span>
                             <button onClick={() => setQuantity(quantity + 1)} className="flex-1 h-full hover:bg-gray-50 flex items-center justify-center text-gray-500"><Plus size={14}/></button>
                         </div>
                         <button 
                             onClick={handleAddToCart}
                             className="flex-1 h-12 bg-black text-white uppercase tracking-widest text-xs font-bold hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                         >
                             <ShoppingBag size={16}/> Dodaj do koszyka
                         </button>
                     </div>
                </div>

                {/* SPECIFICATION TABLE (VIEW MODE) */}
                {activeFiltersForProduct && activeFiltersForProduct.length > 0 && (
                    <div className="mb-8">
                        <h3 className="text-lg font-serif mb-4">Specyfikacja</h3>
                        <table className="w-full text-sm">
                            <tbody>
                                {activeFiltersForProduct.map((cat: any) => {
                                    // Logic for displaying spec value:
                                    // 1. Try currently selected variant
                                    // 2. Fallback to main product attribute
                                    const variantVal = selectedVariant?.attributes?.[cat.key];
                                    const mainVal = product.attributes?.[cat.key];
                                    const val = variantVal || mainVal;

                                    if (!val) return null;
                                    return (
                                        <tr key={cat.key} className="border-b border-gray-100">
                                            <td className="py-2 text-gray-500 w-1/3">{cat.label}</td>
                                            <td className="py-2 font-medium text-gray-900">{val}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Long Description */}
                <div>
                     <h3 className="text-lg font-serif mb-4">Opis Szczegółowy</h3>
                     {isEditMode ? (
                         <textarea 
                             value={product.longDescription || ''}
                             onChange={(e) => handleUpdateField('longDescription', e.target.value)}
                             placeholder="Wpisz szczegółowy opis produktu (obsługuje HTML)..."
                             className="w-full h-64 border border-gray-200 p-4 text-sm leading-relaxed"
                         />
                     ) : (
                         <div 
                             className="text-gray-600 text-sm leading-relaxed"
                             dangerouslySetInnerHTML={{ __html: product.longDescription || product.description }}
                         />
                     )}
                </div>

                {/* EDIT MODE PANELS */}
                {isEditMode && (
                    <div className="mt-12 space-y-8 animate-fade-in">
                        
                        {/* 0. Operations Panel */}
                        <div className="bg-gray-800 p-4 rounded border border-gray-700 text-white flex items-center justify-between">
                            <div>
                                <h4 className="text-sm font-bold flex items-center gap-2 text-gray-200">
                                    <Settings size={16}/> Zarządzanie Produktem
                                </h4>
                                <p className="text-[10px] text-gray-400 mt-1">ID: {product.id}</p>
                            </div>
                            <button 
                                onClick={handleDuplicate}
                                className="bg-white text-black hover:bg-gray-200 px-4 py-2 rounded text-xs font-bold transition-colors flex items-center gap-2"
                            >
                                <Copy size={14} /> Duplikuj Produkt
                            </button>
                        </div>

                        {/* 1. Logistics Panel (NEW) */}
                        <div className="bg-yellow-50 p-6 rounded border border-yellow-200">
                            <h4 className="text-sm font-bold text-yellow-800 mb-4 flex items-center gap-2">
                                <Truck size={16}/> Logistyka i Dostawa
                            </h4>
                            <div>
                                <label className="block text-xs font-bold text-gray-600 mb-1">Gabaryt Wysyłki</label>
                                <select 
                                    value={shippingSize}
                                    onChange={(e) => handleUpdateField('shippingSize', e.target.value)}
                                    className="w-full border rounded px-3 py-2 text-sm bg-white"
                                >
                                    <option value="parcel_locker">Paczkomat + Kurier (Mały/Średni)</option>
                                    <option value="courier_only">Tylko Kurier (Duży/Gabaryt)</option>
                                </select>
                                <p className="text-[10px] text-yellow-600 mt-2">
                                    Wybierz "Tylko Kurier", jeśli produkt jest za duży do Paczkomatu (np. duży zmiękczacz, worki soli). 
                                    Zablokuje to wybór Paczkomatu w koszyku.
                                </p>
                            </div>
                        </div>

                        {/* 2. Attributes Manager (Main Product) */}
                        <div className="bg-orange-50 p-6 rounded border border-orange-200">
                                <div className="flex justify-between items-center mb-6">
                                    <h4 className="text-sm font-bold text-orange-800 flex items-center gap-2">
                                        <SlidersHorizontal size={16}/> Cechy i Specyfikacja
                                    </h4>
                                    <div className="flex items-center gap-2">
                                        <button 
                                            onClick={handleAiAutoFillParams}
                                            disabled={isFetchingAiParams || !product.title}
                                            className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50 border border-indigo-200 bg-white px-2 py-1 rounded shadow-sm transition-colors"
                                            title="Pobierz i dopasuj parametry z użyciem wyszukiwania w interencie."
                                        >
                                            {isFetchingAiParams ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                            Uzupełnij AI z wyszukiwarki
                                        </button>
                                        <button 
                                            onClick={() => setIsAttrManagerOpen(true)}
                                            className="text-xs bg-orange-200 text-orange-900 hover:bg-orange-300 px-2 py-1 rounded flex items-center gap-1 font-bold transition-colors"
                                        >
                                            <List size={12}/> Zarządzaj Cechami Globalnymi
                                        </button>
                                    </div>
                                </div>
                                {(!activeFiltersForProduct || activeFiltersForProduct.length === 0) ? (
                                    <p className="text-xs text-orange-600">
                                        W tej kategorii (sekcji) nie zdefiniowano jeszcze cech. 
                                        Wróć do strony głównej edycji by je dodać.
                                    </p>
                                ) : (
                                    <div className="grid grid-cols-2 gap-4">
                                        {activeFiltersForProduct.map((cat: any) => (
                                            <div key={cat.key}>
                                                <label className="block text-xs font-bold text-gray-600 mb-1">{cat.label}</label>
                                                {cat.options && (cat.options as string[]).length > 0 ? (
                                                    <select 
                                                        value={product.attributes?.[cat.key] || ''}
                                                        onChange={(e) => handleUpdateAttribute(cat.key, e.target.value)}
                                                        className="w-full border rounded px-3 py-2 text-sm bg-white"
                                                    >
                                                        <option value="">-- Wybierz --</option>
                                                        {cat.options.map((opt: string) => (
                                                            <option key={opt} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input 
                                                        value={product.attributes?.[cat.key] || ''}
                                                        onChange={(e) => handleUpdateAttribute(cat.key, e.target.value)}
                                                        className="w-full border rounded px-3 py-2 text-sm"
                                                        placeholder={`Wpisz ${cat.label}...`}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                        </div>

                        {/* 3. Pricing & Analytics Panel */}
                        <div className="bg-green-50 p-6 rounded border border-green-200">
                             <h4 className="text-sm font-bold text-green-800 mb-6 flex items-center gap-2">
                                <DollarSign size={16}/> Analityka i Ceny
                             </h4>
                             <div className="grid grid-cols-2 gap-6 mb-4">
                                 <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1">Cena Sprzedaży</label>
                                    <input 
                                       value={currentPriceStr}
                                       disabled
                                       className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-500 font-mono"
                                    />
                                    {product.variants?.length > 0 && <p className="text-[10px] text-gray-500 mt-1">Edytuj w wariantach</p>}
                                 </div>
                                 <div>
                                     <label className="block text-xs font-bold text-gray-600 mb-1">Cena Zakupu (Netto/Brutto)</label>
                                     <input 
                                        value={purchasePriceStr || ''}
                                        onChange={(e) => {
                                            if (selectedVariantId) {
                                                handleUpdateVariant(selectedVariantId, 'purchasePrice', e.target.value);
                                            } else {
                                                handleUpdateField('purchasePrice', e.target.value);
                                            }
                                        }}
                                        placeholder="0.00"
                                        className="w-full border rounded px-3 py-2 font-mono focus:ring-2 focus:ring-green-500 outline-none"
                                     />
                                 </div>
                             </div>
                             
                             <div className="flex gap-4 p-4 bg-white rounded border border-green-100">
                                 <div className="flex-1 text-center border-r border-gray-100">
                                     <p className="text-xs text-gray-500 uppercase tracking-widest">Zysk / Szt.</p>
                                     <p className="text-xl font-bold text-green-600">{Math.round(profit)} PLN</p>
                                 </div>
                                 <div className="flex-1 text-center">
                                     <p className="text-xs text-gray-500 uppercase tracking-widest">Marża</p>
                                     <p className={`text-xl font-bold ${parseFloat(margin) > 20 ? 'text-green-600' : 'text-orange-500'}`}>{margin}%</p>
                                 </div>
                             </div>
                        </div>

                        {/* 4. Promotion Manager */}
                        <div className="bg-purple-50 p-6 rounded border border-purple-200">
                             <h4 className="text-sm font-bold text-purple-800 mb-6 flex items-center gap-2">
                                <Tag size={16}/> Promocje i Obniżki
                             </h4>

                             {product.discountConfig?.isActive ? (
                                 <div className="bg-white p-4 rounded border border-purple-100 flex justify-between items-center">
                                     <div>
                                         <p className="font-bold text-purple-900">Promocja Aktywna</p>
                                         <p className="text-xs text-gray-600">
                                            Typ: {product.discountConfig.type === 'PERCENT' ? 'Procentowa' : 'Kwotowa'} ({product.discountConfig.value})
                                         </p>
                                         {(product.discountConfig.endDate) && <p className="text-xs text-red-500">Do: {product.discountConfig.endDate}</p>}
                                     </div>
                                     <button onClick={removeDiscount} className="text-xs bg-red-100 text-red-600 px-3 py-2 rounded font-bold hover:bg-red-200">
                                         Zakończ Promocję
                                     </button>
                                 </div>
                             ) : (
                                 <div className="space-y-4">
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Typ Promocji</label>
                                            <div className="flex border rounded bg-white overflow-hidden">
                                                <button 
                                                    onClick={() => setPromoType('PERCENT')} 
                                                    className={`flex-1 py-2 text-xs font-bold ${promoType === 'PERCENT' ? 'bg-purple-600 text-white' : 'hover:bg-gray-50'}`}
                                                >
                                                    % Procent
                                                </button>
                                                <button 
                                                    onClick={() => setPromoType('FIXED')}
                                                    className={`flex-1 py-2 text-xs font-bold ${promoType === 'FIXED' ? 'bg-purple-600 text-white' : 'hover:bg-gray-50'}`}
                                                >
                                                    PLN Kwota
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Wartość Obniżki</label>
                                            <div className="relative">
                                                <input 
                                                    value={promoValue}
                                                    onChange={(e) => setPromoValue(e.target.value)}
                                                    className="w-full border rounded px-3 py-2 pl-8"
                                                    placeholder={promoType === 'PERCENT' ? 'np. 20' : 'np. 500'}
                                                />
                                                <div className="absolute left-2 top-2.5 text-gray-400">
                                                    {promoType === 'PERCENT' ? <Percent size={14}/> : <DollarSign size={14}/>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Data Od (Opcjonalnie)</label>
                                            <input type="date" value={promoStart} onChange={e => setPromoStart(e.target.value)} className="w-full border rounded px-2 py-1 bg-white text-sm"/>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-600 mb-1">Data Do (Opcjonalnie)</label>
                                            <input type="date" value={promoEnd} onChange={e => setPromoEnd(e.target.value)} className="w-full border rounded px-2 py-1 bg-white text-sm"/>
                                        </div>
                                    </div>
                                    
                                    <div>
                                         <label className="block text-xs font-bold text-gray-600 mb-1">Limit Ilościowy (np. pierwsze 5 szt.)</label>
                                         <input type="number" value={promoLimit} onChange={e => setPromoLimit(e.target.value)} className="w-full border rounded px-2 py-1 bg-white text-sm" placeholder="Bez limitu"/>
                                    </div>

                                    <button onClick={applyDiscount} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded font-bold text-xs uppercase tracking-wider transition-colors">
                                        Aktywuj Promocję
                                    </button>
                                 </div>
                             )}
                        </div>

                        {/* 5. Variant Manager (Updated with Attribute Editor) */}
                        <div className="bg-blue-50 p-6 rounded border border-blue-100">
                            <h4 className="text-sm font-bold text-blue-800 mb-4 flex items-center gap-2"><Settings size={14}/> Zarządzanie Wariantami</h4>
                            <div className="space-y-4">
                                {product.variants?.map((v: any) => (
                                    <div key={v.id} className="bg-white p-3 border border-blue-200 rounded shadow-sm">
                                        <div className="flex gap-2 items-center mb-2">
                                            <div className="flex-1">
                                                <label className="text-[9px] text-gray-400 block">Nazwa Wariantu</label>
                                                <input 
                                                    value={v.name}
                                                    onChange={(e) => handleUpdateVariant(v.id, 'name', e.target.value)}
                                                    className="text-xs font-bold border-b border-transparent focus:border-blue-500 w-full"
                                                />
                                            </div>
                                            <div className="w-20">
                                                <label className="text-[9px] text-gray-400 block">Cena Sprz.</label>
                                                <input 
                                                    value={v.price}
                                                    onChange={(e) => handleUpdateVariant(v.id, 'price', e.target.value)}
                                                    className="text-xs w-full border-b border-transparent focus:border-blue-500 text-right"
                                                />
                                            </div>
                                            <div className="w-20">
                                                <label className="text-[9px] text-gray-400 block">Cena Zak.</label>
                                                <input 
                                                    value={v.purchasePrice || ''}
                                                    onChange={(e) => handleUpdateVariant(v.id, 'purchasePrice', e.target.value)}
                                                    placeholder="Koszt"
                                                    className="text-xs w-full border-b border-transparent focus:border-blue-500 text-right text-gray-500"
                                                />
                                            </div>
                                            <button onClick={() => handleRemoveVariant(v.id)} className="text-red-400 hover:text-red-600 p-1 ml-2"><Trash2 size={12}/></button>
                                        </div>
                                        
                                        {/* ATTRIBUTE EDITOR FOR VARIANT */}
                                        {activeFiltersForProduct && activeFiltersForProduct.length > 0 && (
                                            <div className="mt-2 pt-2 border-t border-gray-100">
                                                <p className="text-[9px] font-bold text-gray-400 uppercase mb-1">Cechy wariantu (dla filtrów)</p>
                                                <div className="grid grid-cols-2 gap-2">
                                                    {activeFiltersForProduct.map((cat: any) => (
                                                        <div key={cat.key}>
                                                            <label className="text-[8px] text-gray-400 block truncate">{cat.label}</label>
                                                            {cat.options && (cat.options as string[]).length > 0 ? (
                                                                <select 
                                                                    value={v.attributes?.[cat.key] || ''}
                                                                    onChange={(e) => handleUpdateVariantAttribute(v.id, cat.key, e.target.value)}
                                                                    className="text-xs border rounded w-full px-1 py-0.5 bg-white"
                                                                >
                                                                    <option value="">-</option>
                                                                    {cat.options.map((opt: string) => (
                                                                        <option key={opt} value={opt}>{opt}</option>
                                                                    ))}
                                                                </select>
                                                            ) : (
                                                                <input 
                                                                    value={v.attributes?.[cat.key] || ''}
                                                                    onChange={(e) => handleUpdateVariantAttribute(v.id, cat.key, e.target.value)}
                                                                    className="text-xs border rounded w-full px-1 py-0.5"
                                                                    placeholder="-"
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button onClick={handleAddVariant} className="mt-4 text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                <Plus size={14}/> Dodaj Wariant
                            </button>
                        </div>

                        {/* 6. Related Products Manager (Existing) */}
                        <div className="bg-gray-100 p-6 rounded border border-gray-200">
                             <div className="flex items-center justify-between mb-4">
                               <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2"><LinkIcon size={14}/> Produkty Powiązane (Rekomendowane)</h4>
                               <button 
                                 onClick={handleSuggestRelatedProducts}
                                 disabled={isSuggestingRelated}
                                 className="text-xs font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50 border border-indigo-200 bg-white px-2 py-1 rounded shadow-sm"
                               >
                                 {isSuggestingRelated ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                 Zasugeruj AI
                               </button>
                             </div>
                             <div className="max-h-48 overflow-y-auto space-y-1 bg-white p-2 rounded border border-gray-300">
                                 {allProducts.filter((p: any) => p.id !== product.id).map((p: any) => {
                                     const isSelected = (product.relatedProductIds || []).includes(p.id);
                                     return (
                                         <div 
                                            key={p.id} 
                                            className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50 border-blue-100' : ''}`}
                                            onClick={() => toggleRelatedProduct(p.id)}
                                         >
                                             <div className={`w-4 h-4 border rounded flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                                 {isSelected && <Check size={10} className="text-white"/>}
                                             </div>
                                             <img src={p.imageUrl} className="w-8 h-8 object-cover rounded" alt=""/>
                                             <div className="flex-1">
                                                <p className="text-xs font-bold truncate">{p.title}</p>
                                                <p className="text-[10px] text-gray-500">{p.price} PLN</p>
                                             </div>
                                         </div>
                                     );
                                 })}
                             </div>
                        </div>
                    </div>
                )}

            </div>
        </div>

        {/* RELATED PRODUCTS SECTION (VIEW MODE) */}
        {relatedProducts.length > 0 && (
            <div className="mt-24 border-t border-gray-100 pt-16">
                 <h2 className="text-2xl font-serif text-gray-900 mb-12 text-center">Podobne produkty</h2>
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                     {relatedProducts.map((relProd: any) => (
                         <div key={relProd.id} className="group flex flex-col relative">
                              <Link to={`/product/${relProd.id}`} className="block aspect-[4/5] bg-[#f5f5f5] mb-4 overflow-hidden">
                                  <img 
                                     src={relProd.imageUrl} 
                                     alt={relProd.title}
                                     className="w-full h-full object-cover mix-blend-multiply group-hover:scale-105 transition-transform duration-700 ease-in-out"
                                  />
                              </Link>
                              <div className="text-left">
                                  <Link to={`/product/${relProd.id}`} className="block">
                                      <h3 className="font-serif text-base text-gray-900 mb-1 leading-snug group-hover:underline decoration-1 underline-offset-4">{relProd.title}</h3>
                                      <p className="text-xs font-bold text-black">{relProd.price} PLN</p>
                                  </Link>
                              </div>
                         </div>
                     ))}
                 </div>
            </div>
        )}

        {/* RECENTLY VIEWED PRODUCTS SECTION */}
        {!isEditMode && recentlyViewedProducts.length > 0 && (
            <div className="mt-24 border-t border-gray-100 pt-16">
                 <div className="flex justify-between items-end mb-12 border-b border-gray-100 pb-4">
                     <h2 className="text-2xl font-serif text-gray-900">Ostatnio przeglądane</h2>
                 </div>
                 <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                     {recentlyViewedProducts.map((rvProd: any) => (
                         <Link key={rvProd.id} to={`/product/${rvProd.id}`} className="group flex flex-col relative">
                              <div className="aspect-square bg-[#f5f5f5] mb-3 overflow-hidden">
                                  <img 
                                     src={rvProd.imageUrl} 
                                     alt={rvProd.title}
                                     className="w-full h-full object-cover mix-blend-multiply group-hover:scale-105 transition-transform duration-500 ease-in-out"
                                  />
                              </div>
                              <div className="text-left">
                                  <h3 className="font-sans text-xs text-gray-900 mb-1 group-hover:underline decoration-1 underline-offset-4 truncate">{rvProd.title}</h3>
                                  <p className="text-xs font-medium text-gray-500">{rvProd.price} PLN</p>
                              </div>
                         </Link>
                     ))}
                 </div>
            </div>
        )}

        <ImagePickerModal 
            isOpen={isImageModalOpen}
            onClose={() => setIsImageModalOpen(false)}
            onSave={handleImageSave}
            initialUrl={gallery[activeImageIndex]}
            contextName={product.title}
            allowMultiple={activeImageIndex >= gallery.length} // Only allow multiple when adding new
        />

        {/* ATTRIBUTE MANAGER MODAL */}
        <AttributeManagerModal 
            isOpen={isAttrManagerOpen} 
            onClose={() => setIsAttrManagerOpen(false)}
            section={currentSection}
            onSave={(newFilters) => updateSection(pageId, sectionId, { filterCategories: newFilters })}
        />
    </div>
  );
};

// Helper components
const XIcon = ({size}: {size:number}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
);

const AttributeManagerModal = ({ 
    isOpen, onClose, section, onSave 
}: { 
    isOpen: boolean, onClose: () => void, section: any, onSave: (filters: any[]) => void 
}) => {
    const [newLabel, setNewLabel] = useState('');
    const [newOptions, setNewOptions] = useState('');

    if (!isOpen || !section) return null;

    const currentFilters = section.filterCategories || [];

    const handleAdd = () => {
        if (!newLabel) return;
        const key = newLabel.toLowerCase()
            .replace(/ą/g, 'a').replace(/ć/g, 'c').replace(/ę/g, 'e').replace(/ł/g, 'l').replace(/ń/g, 'n').replace(/ó/g, 'o').replace(/ś/g, 's').replace(/ź/g, 'z').replace(/ż/g, 'z')
            .replace(/[^a-z0-9]+/g, '_');
        
        const options = newOptions.split(',').map(s => s.trim()).filter(s => s.length > 0);
        
        onSave([...currentFilters, { key, label: newLabel, options }]);
        setNewLabel('');
        setNewOptions('');
    };

    const handleRemove = (key: string) => {
        if (confirm('Usunięcie tej cechy usunie ją z filtrów, ale nie usunie danych zapisanych w produktach. Kontynuować?')) {
            onSave(currentFilters.filter((f: any) => f.key !== key));
        }
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-4 border-b bg-orange-50 flex justify-between items-center">
                    <h3 className="font-bold text-orange-900 flex items-center gap-2">
                        <List size={20} /> Menedżer Cech Kategorii
                    </h3>
                    <button onClick={onClose}><X size={20}/></button>
                </div>
                <div className="p-6 overflow-y-auto space-y-6">
                    <div className="bg-orange-50 p-4 rounded border border-orange-100">
                        <p className="text-xs text-orange-800 mb-3 font-bold uppercase tracking-wide">Dodaj nową cechę</p>
                        <div className="space-y-2">
                            <input 
                                className="w-full border rounded px-3 py-2 text-sm"
                                placeholder="Nazwa (np. Kolor, Średnica, Moc)"
                                value={newLabel}
                                onChange={e => setNewLabel(e.target.value)}
                            />
                            <input 
                                className="w-full border rounded px-3 py-2 text-sm"
                                placeholder="Opcje po przecinku (np. Biały, Czarny, Stalowy)"
                                value={newOptions}
                                onChange={e => setNewOptions(e.target.value)}
                            />
                            <button onClick={handleAdd} className="w-full bg-orange-600 text-white py-2 rounded font-bold text-xs uppercase hover:bg-orange-700">
                                Dodaj Cechę
                            </button>
                        </div>
                    </div>

                    <div>
                        <p className="text-xs text-gray-400 mb-2 font-bold uppercase tracking-wide">Istniejące Cechy</p>
                        <div className="space-y-2">
                            {currentFilters.map((f: any) => (
                                <div key={f.key} className="flex items-center justify-between p-3 border rounded bg-gray-50">
                                    <div>
                                        <p className="font-bold text-sm text-gray-800">{f.label}</p>
                                        <p className="text-[10px] text-gray-500">{f.options?.join(', ') || 'Pole tekstowe (brak opcji)'}</p>
                                    </div>
                                    <button onClick={() => handleRemove(f.key)} className="text-red-400 hover:text-red-600 p-2">
                                        <Trash2 size={14}/>
                                    </button>
                                </div>
                            ))}
                            {currentFilters.length === 0 && <p className="text-center text-sm text-gray-400 py-4">Brak zdefiniowanych cech.</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailsPage;